import { useCallback, useEffect, useMemo, useState } from "react";
import type { MatchListItem, InningsSummary, Tournament } from "@sportsos/engine";
import { api, ApiRequestError, } from "../api.ts";
import {
  dismissValueProps,
  homeMode,
  VALUE_PROPS,
  valuePropsDismissed,
} from "../home/home.ts";
import { localStore } from "../localStore.ts";
import { groupMatches } from "../match/grouping.ts";

interface Props {
  readonly userName: string | null;
  readonly onNewMatch: () => void;
  readonly onSessionLost: () => void;
  readonly resumeLabel: string | null;
  readonly onResume: () => void;
  readonly onOpenMatch: (matchId: string) => void;
  /**
   * Someone else's match, from the public feeds. Their ledger is not this user's to read
   * through the API, so it opens the public scorecard by shortcode instead — which also
   * works for a guest, who has no session at all.
   */
  readonly onOpenPublicMatch: (shortcode: string) => void;
  readonly onNewTournament: () => void;
  readonly onOpenTournament: (tournamentId: string) => void;
  readonly guest: boolean;
}

const RECENT_SHOWN = 10;

/**
 * How often the Live / Recent lists re-ask while the screen is open and in front.
 *
 * Without this they were read once, when Home mounted — so a match someone started a minute
 * later never appeared for a person already looking at the list, and "Live now" went stale
 * the moment it was drawn. Thirty seconds is a trade: quick enough that a new match shows up
 * while you are still looking, slow enough to cost a phone on a ground almost nothing (the
 * responses are a few KB and a hidden tab asks nothing at all).
 */
const FEED_REFRESH_MS = 30_000;

import { avatarColor, firstName, initials } from "./shared.ts";

export function Home({
  userName,
  onNewMatch,
  onSessionLost,
  resumeLabel,
  onResume,
  onOpenMatch,
  onOpenPublicMatch,
  onNewTournament,
  onOpenTournament,
  guest,
}: Props): React.ReactElement {
  const [matches, setMatches] = useState<readonly MatchListItem[] | null>(null);
  const [live, setLive] = useState<readonly MatchListItem[]>([]);
  const [publicRecent, setPublicRecent] = useState<readonly MatchListItem[]>([]);
  const [tournaments, setTournaments] = useState<readonly Tournament[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const store = useMemo(() => localStore(), []);
  const [propsShown, setPropsShown] = useState(() => !valuePropsDismissed(store));

  const load = useCallback(async (): Promise<void> => {
    if (guest) {
      setMatches([]);
      return;
    }
    setProblem(null);
    try {
      const { matches: rows } = await api.listMatches();
      setMatches(rows);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.status === 401) {
        onSessionLost();
        return;
      }
      setMatches([]);
      setProblem("Could not load your matches. Check your connection and try again.");
    }
  }, [guest, onSessionLost]);

  useEffect(() => {
    void load();
  }, [load]);

  // The public feeds. A failed refresh keeps what is already on screen rather than blanking
  // it: a list that empties itself when the signal blips is worse than one a minute stale.
  useEffect(() => {
    let mounted = true;
    const refresh = (): void => {
      void api
        .liveMatches()
        .then(({ matches: rows }) => {
          if (mounted) setLive(rows);
        })
        .catch(() => undefined);
      void api
        .recentMatches()
        .then(({ matches: rows }) => {
          if (mounted) setPublicRecent(rows);
        })
        .catch(() => undefined);
    };
    const refreshIfVisible = (): void => {
      if (document.visibilityState === "visible") refresh();
    };

    refresh();
    document.addEventListener("visibilitychange", refreshIfVisible);
    const timer = window.setInterval(refreshIfVisible, FEED_REFRESH_MS);
    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!guest) {
      void api
        .listTournaments()
        .then(({ tournaments: rows }) => {
          if (mounted) setTournaments(rows);
        })
        .catch(() => undefined);
    }

    return () => {
      mounted = false;
    };
  }, [guest, store]);

  const groups = useMemo(() => groupMatches(matches ?? [], Date.now()), [matches]);

  const ownLive = useMemo(
    () => (matches ?? []).filter((m) => m.status !== "completed"),
    [matches],
  );
  const allLive = useMemo(() => {
    const liveIds = new Set(ownLive.map((m) => m.id));
    return [...ownLive, ...live.filter((m) => !liveIds.has(m.id))];
  }, [ownLive, live]);

  // The user's own completed matches and everyone else's, newest first. Own rows win a
  // duplicate: they come from the scorer's list, which is the one that opens in-app.
  const recent = useMemo(() => {
    const own = [...groups.recent, ...groups.earlier].filter((m) => m.status === "completed");
    const ownIds = new Set(own.map((m) => m.id));
    return [...own, ...publicRecent.filter((m) => !ownIds.has(m.id))]
      .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
      .slice(0, RECENT_SHOWN);
  }, [groups, publicRecent]);

  const ownIds = useMemo(() => new Set((matches ?? []).map((m) => m.id)), [matches]);
  const openCard = useCallback(
    (match: MatchListItem): void => {
      if (ownIds.has(match.id)) onOpenMatch(match.id);
      else onOpenPublicMatch(match.shortcode);
    },
    [ownIds, onOpenMatch, onOpenPublicMatch],
  );

  const mode = homeMode({
    matchCount: matches === null ? null : matches.length,
    hasActiveMatch: resumeLabel !== null,
  });

  const greeting = userName ? `Hey, ${firstName(userName)}` : guest ? "Welcome" : "Hey, scorer";
  const avatarText = userName ? initials(userName) : guest ? "G" : "U";
  const avatarHue = userName ? avatarColor(userName) : "amber";

  return (
    <div className="app-page">
      {/* ── App bar ──────────────────────────────────────────────────── */}
      <header className="ds-appbar">
        <span className={`ds-avatar ds-avatar--${avatarHue}`}>{avatarText}</span>
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">{greeting}</b>
          <span className="ds-appbar__meta" style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" /><circle cx="12" cy="10" r="2.6" />
            </svg>
            CricAI.co
          </span>
        </span>
      </header>

      {problem !== null && (
        <div className="ds-card" style={{ marginBottom: "var(--ds-space-4)" }}>
          <p style={{ color: "var(--ds-danger-600)", marginBottom: "var(--ds-space-2)" }}>{problem}</p>
          <button className="ds-btn ds-btn--ghost ds-btn--sm" onClick={() => void load()}>Try again</button>
        </div>
      )}

      {mode === "new" ? (
        <>
          <Hero onStart={onNewMatch} showSample={allLive.length === 0 && recent.length === 0} />
          {propsShown && (
            <ValueProps onDismiss={() => { dismissValueProps(store); setPropsShown(false); }} />
          )}
          <QuickActions onScore={onNewMatch} onTournament={onNewTournament} guest={guest} />
          <LiveSection matches={allLive} onOpen={openCard} />
          <RecentMatches matches={recent} onOpen={openCard} />
        </>
      ) : (
        <>
          {/* ── Resume in-progress match ────────────────────────────── */}
          {resumeLabel !== null && (
            <article
              className="ds-card"
              style={{ padding: "var(--ds-space-4)", marginBottom: "var(--ds-space-4)", cursor: "pointer" }}
              onClick={onResume}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onResume(); }}
            >
              <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
                <span className="ds-tag ds-tag--live" style={{ flex: "none" }}>
                  <i className="ds-dot" />In progress
                </span>
              </div>
              <div style={{ fontWeight: 700, fontSize: "var(--ds-text-lg)", marginBottom: "var(--ds-space-2)" }}>
                {resumeLabel}
              </div>
              <div className="ds-caption" style={{ marginBottom: "var(--ds-space-3)" }}>
                On this device — pick up where you left off
              </div>
              <span className="ds-btn ds-btn--primary ds-btn--sm">Resume scoring</span>
            </article>
          )}

          {/* ── Live now ────────────────────────────────────────────── */}
          <LiveSection matches={allLive} onOpen={openCard} />

          {/* ── Tournaments ─────────────────────────────────────────── */}
          {!guest && <TournamentSection tournaments={tournaments} onOpen={onOpenTournament} />}

          {/* ── Recent matches ──────────────────────────────────────── */}
          <RecentMatches matches={recent} onOpen={openCard} />
          {matches === null && (
            <div className="ds-caption" style={{ padding: "var(--ds-space-4) 0" }}>Loading...</div>
          )}

          {/* ── Quick action tiles ──────────────────────────────────── */}
          <QuickActions onScore={onNewMatch} onTournament={onNewTournament} guest={guest} />
        </>
      )}

    </div>
  );
}

/**
 * `showSample` is the made-up "Tigers v Lions" card. It only fills the gap when there is no
 * real live or recent match to show — otherwise a new user would see an invented match
 * sitting above the real ones.
 */
function Hero({
  onStart,
  showSample,
}: {
  readonly onStart: () => void;
  readonly showSample: boolean;
}): React.ReactElement {
  return (
    <section style={{ marginBottom: "var(--ds-space-5)" }}>
      <h2 className="ds-heading" style={{ marginBottom: "var(--ds-space-2)" }}>
        Create your first match
      </h2>
      <p className="ds-caption" style={{ marginBottom: "var(--ds-space-4)" }}>
        Ball by ball, on your phone, offline. Full scorecard, shareable link, free.
      </p>
      <button className="ds-btn ds-btn--primary ds-btn--block" onClick={onStart}>
        Create your first match
      </button>

      {showSample && (
      <article
        className="ds-card"
        aria-hidden="true"
        style={{ padding: "var(--ds-space-4)", marginTop: "var(--ds-space-4)" }}
      >
        <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
          <span style={{ fontWeight: 700 }}>Tigers v Lions</span>
          <span className="spacer" />
          <span className="ds-tag ds-tag--live"><i className="ds-dot" />Live</span>
        </div>
        <div className="ds-datarow ds-datarow--active">
          <span className="ds-avatar ds-avatar--sm ds-avatar--amber">TI</span>
          <span className="ds-datarow__name">Tigers</span>
          <span className="ds-datarow__value">142/4</span>
          <span className="ds-datarow__sub">(16.3)</span>
        </div>
        <div className="ds-datarow">
          <span className="ds-avatar ds-avatar--sm ds-avatar--teal">LI</span>
          <span className="ds-datarow__name">Lions</span>
          <span className="ds-datarow__value">104/3</span>
          <span className="ds-datarow__sub">(12.0)</span>
        </div>
      </article>
      )}
    </section>
  );
}

function ValueProps({ onDismiss }: { readonly onDismiss: () => void }): React.ReactElement {
  return (
    <div className="ds-card" style={{ padding: "var(--ds-space-4)", marginBottom: "var(--ds-space-4)" }}>
      <ul style={{ margin: 0, paddingLeft: "var(--ds-space-5)" }}>
        {VALUE_PROPS.map((prop) => (
          <li key={prop} style={{ marginBottom: "var(--ds-space-1)" }}>{prop}</li>
        ))}
      </ul>
      <button className="ds-btn ds-btn--ghost ds-btn--sm" style={{ marginTop: "var(--ds-space-3)" }} onClick={onDismiss}>
        Got it
      </button>
    </div>
  );
}

function TrophyIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4.5v1.5A3.5 3.5 0 0 0 8 11" />
      <path d="M17 6h2.5v1.5A3.5 3.5 0 0 1 16 11" />
      <path d="M12 14v3" />
      <path d="M8.5 20h7" />
    </svg>
  );
}

function QuickActions({
  onScore,
  onTournament,
  guest,
}: {
  readonly onScore: () => void;
  readonly onTournament: () => void;
  readonly guest: boolean;
}): React.ReactElement {
  return (
    <div style={{ marginTop: "var(--ds-space-5)", display: "flex", flexDirection: "column", gap: "var(--ds-space-3)" }}>
      <button
        className="ds-tile ds-tile--azure"
        style={{ width: "100%" }}
        onClick={onScore}
      >
        <span className="ds-tile__title" style={{ maxWidth: "74%" }}>Create a match</span>
        <span className="ds-tile__meta" style={{ maxWidth: "74%" }}>Two teams, the toss, and go — ball by ball on your phone.</span>
      </button>
      {!guest && (
        <button
          className="ds-tile ds-tile--magenta"
          style={{ width: "100%" }}
          onClick={onTournament}
        >
          <span className="ds-tile__title" style={{ maxWidth: "74%" }}>Create a tournament</span>
          <span className="ds-tile__meta" style={{ maxWidth: "74%" }}>Fixtures, points table and a public link — in about a minute.</span>
        </button>
      )}
    </div>
  );
}

/* ── Live now — horizontal card rail ──────────────────────────────────────── */

function LiveSection({
  matches,
  onOpen,
}: {
  readonly matches: readonly MatchListItem[];
  readonly onOpen: (match: MatchListItem) => void;
}): React.ReactElement {
  return (
    <div style={{ marginTop: "var(--ds-space-5)" }}>
      <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
        <h2 className="ds-heading" style={{ flex: 1 }}>Live now</h2>
        {matches.length > 0 && (
          <button className="ds-btn ds-btn--ghost ds-btn--sm">See all</button>
        )}
      </div>
      {matches.length === 0 ? (
        <div className="ds-card ds-card--flat" style={{ padding: "var(--ds-space-8) var(--ds-space-4)", textAlign: "center" }}>
          <svg
            viewBox="0 0 48 32"
            aria-hidden="true"
            focusable="false"
            style={{ width: 48, height: 32, margin: "0 auto var(--ds-space-3)", display: "block", fill: "none", stroke: "var(--ds-text-muted)", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}
          >
            <path d="M18 26V8M24 26V8M30 26V8" />
            <path d="M16 8h16" />
            <circle cx="8" cy="24" r="3" />
          </svg>
          <div className="ds-caption">No live matches right now.</div>
          <div className="ds-caption" style={{ marginTop: "var(--ds-space-1)" }}>Create a match to get started.</div>
        </div>
      ) : (
        <div className="ds-hscroll ds-hscroll--bleed">
          {matches.map((match) => (
            <MatchCard key={match.id} match={match} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function MatchCard({
  match,
  onOpen,
}: {
  readonly match: MatchListItem;
  readonly onOpen: (match: MatchListItem) => void;
}): React.ReactElement {
  const colorA = avatarColor(match.teamAName);
  const colorB = avatarColor(match.teamBName);
  const live = match.status !== "completed";

  const regular = match.innings.filter((inn) => !inn.superOver);
  const superOvers = match.innings.filter((inn) => inn.superOver);

  const teamAInnings = regular.find((inn) => inn.battingTeamName === match.teamAName) ?? (regular[0]?.battingTeamName ? undefined : regular[0]);
  const teamBInnings = regular.find((inn) => inn.battingTeamName === match.teamBName) ?? (regular[1]?.battingTeamName ? undefined : regular[1]);
  const teamASO = superOvers.find((inn) => inn.battingTeamName === match.teamAName) ?? (superOvers[0]?.battingTeamName ? undefined : superOvers[0]);
  const teamBSO = superOvers.find((inn) => inn.battingTeamName === match.teamBName) ?? (superOvers[1]?.battingTeamName ? undefined : superOvers[1]);

  const formatScore = (inn: InningsSummary | undefined) =>
    inn ? `${inn.runs}/${inn.wickets}` : "—";
  const formatOversText = (inn: InningsSummary | undefined) =>
    inn ? `(${inn.overs})` : "yet to bat";
  const formatSO = (inn: InningsSummary | undefined) =>
    inn ? `SO: ${inn.runs}/${inn.wickets}` : null;

  const formatLabel = match.ruleSetName;

  return (
    <article
      className="ds-card"
      style={{ width: 272, padding: "var(--ds-space-4)", cursor: "pointer", flex: "none" }}
      onClick={() => onOpen(match)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(match); }}
    >
      {/* Status + format row */}
      <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
        {live ? (
          <span className="ds-tag ds-tag--live" style={{ flex: "none" }}><i className="ds-dot" />Live</span>
        ) : (
          <span className="ds-tag ds-tag--solid" style={{ flex: "none" }}>Completed</span>
        )}
        <span className="ds-caption" style={{ marginLeft: "auto" }}>
          {formatLabel ?? ""}
        </span>
      </div>

      {/* Team A */}
      <div className={`ds-datarow${live ? " ds-datarow--active" : ""}`}>
        <span className={`ds-avatar ds-avatar--sm ds-avatar--${colorA}`}>
          {match.teamAName.slice(0, 2).toUpperCase()}
        </span>
        <span className="ds-datarow__name">{match.teamAName}</span>
        <span className="ds-datarow__value">{formatScore(teamAInnings)}</span>
        <span className="ds-datarow__sub">
          {formatOversText(teamAInnings)}
          {teamASO ? ` · ${formatSO(teamASO)}` : ""}
        </span>
      </div>

      {/* Team B */}
      <div className="ds-datarow">
        <span className={`ds-avatar ds-avatar--sm ds-avatar--${colorB}`}>
          {match.teamBName.slice(0, 2).toUpperCase()}
        </span>
        <span className="ds-datarow__name">{match.teamBName}</span>
        <span className="ds-datarow__value">{formatScore(teamBInnings)}</span>
        <span className="ds-datarow__sub">
          {formatOversText(teamBInnings)}
          {teamBSO ? ` · ${formatSO(teamBSO)}` : ""}
        </span>
      </div>

      {/* Result verdict */}
      {match.resultText !== null && (
        <div style={{
          marginTop: "var(--ds-space-3)",
          paddingTop: "var(--ds-space-2)",
          borderTop: "1px solid var(--ds-border)",
          fontSize: "var(--ds-text-xs)",
          fontWeight: 600,
          color: "var(--ds-success-600)",
        }}>
          {match.resultText}
        </div>
      )}
    </article>
  );
}

/* ── Recent matches — same horizontal card rail as Live now ───────────────── */

function RecentMatches({
  matches,
  onOpen,
}: {
  readonly matches: readonly MatchListItem[];
  readonly onOpen: (match: MatchListItem) => void;
}): React.ReactElement | null {
  if (matches.length === 0) return null;
  return (
    <div style={{ marginTop: "var(--ds-space-5)" }}>
      <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
        <h2 className="ds-heading" style={{ flex: 1 }}>Recent matches</h2>
      </div>
      <div className="ds-hscroll ds-hscroll--bleed">
        {matches.map((match) => (
          <MatchCard key={match.id} match={match} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

/* ── Tournaments section ──────────────────────────────────────────────────── */

function TournamentSection({
  tournaments,
  onOpen,
}: {
  readonly tournaments: readonly Tournament[];
  readonly onOpen: (tournamentId: string) => void;
}): React.ReactElement | null {
  if (tournaments.length === 0) return null;
  return (
    <div style={{ marginTop: "var(--ds-space-5)" }}>
      <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
        <h2 className="ds-heading" style={{ flex: 1 }}>Tournaments nearby</h2>
        <button className="ds-btn ds-btn--ghost ds-btn--sm">See all {tournaments.length}</button>
      </div>
      <div className="ds-list">
        {tournaments.map((tournament) => (
          <button key={tournament.id} className="ds-list-item" onClick={() => onOpen(tournament.id)}>
            <span className="ds-iconslot ds-iconslot--amber" aria-hidden="true">
              <TrophyIcon />
            </span>
            <span className="ds-list-item__body">
              <b className="ds-list-item__title">{tournament.name}</b>
              <span className="ds-list-item__meta">
                {tournament.format === "round_robin" ? "Round robin" : "Groups + knockout"} · {tournament.startDate} to {tournament.endDate}
              </span>
            </span>
            <span className="ds-badge ds-badge--success">{tournament.public ? "Open" : "Private"}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
